'use client'

import { useToastStore } from '@/core/stores/toast.store'
import type { ToastVariant, ToastAction } from '@/core/stores/toast.store'

type ToastOpts = {
  /** 기본값: 'default' */
  variant?: ToastVariant
  /** 자동 닫힘 시간 (ms). 기본: 3000 */
  duration?: number
  /** 스와이프/X 버튼으로 닫기 허용 (기본: true) */
  dismissible?: boolean
  /** 토스트 내 액션 버튼 목록 */
  actions?: ToastAction[]
}

/**
 * 클라이언트 토스트 알림 API. 서버 결과를 받은 클라이언트 이벤트에서 호출한다.
 *
 * @example
 * toast.show('저장되었습니다')
 * toast.success('로그인 완료', { duration: 2000 })
 * toast.error('네트워크 오류', {
 *   actions: [{ id: 'retry', label: '재시도', onClick: retry }],
 * })
 * const id = toast.show('진행 중...'); toast.dismiss(id)
 */
export const toast = {
  /** 기본 variant 토스트. id 반환 (수동 dismiss용) */
  show: (message: string, opts?: ToastOpts): string =>
    useToastStore.getState().showToast({ message, ...opts }),

  /** 성공 토스트 (초록). id 반환 */
  success: (message: string, opts?: Omit<ToastOpts, 'variant'>): string =>
    useToastStore
      .getState()
      .showToast({ message, ...opts, variant: 'success' }),

  /** 에러 토스트 (빨강). id 반환 */
  error: (message: string, opts?: Omit<ToastOpts, 'variant'>): string =>
    useToastStore.getState().showToast({ message, ...opts, variant: 'error' }),

  /** 특정 id 토스트 닫기 */
  dismiss: (id: string): void => useToastStore.getState().dismissToast(id),

  /** 모든 토스트 닫기 */
  clear: (): void => useToastStore.getState().clearToasts(),
}
